import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BadgeCheck, Banknote, Loader2, Link2 } from "lucide-react";

/** Lets a cleaner or vendor link their own bank account so payouts actually land. */
export default function PayoutSetup() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payouts/me/connect").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts/connect/onboarding", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payout setup");
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section className="rounded-lg border border-gold/40 bg-white p-5" data-testid="payout-setup-card">
      <div className="flex items-center gap-2">
        <Banknote className="h-4 w-4 text-gold" />
        <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Get paid</h2>
      </div>
      {status.payouts_enabled ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-emerald-700" data-testid="payout-ready">
          <BadgeCheck className="h-4 w-4" /> Your bank account is connected — payouts land automatically.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            {status.details_submitted
              ? "Stripe still needs a little more information before money can be sent to you."
              : "Link your bank account once and every approved payout is deposited straight to you."}
          </p>
          <Button data-testid="start-payout-setup-btn" disabled={busy} onClick={start}
            className="mt-4 h-11 gap-2 bg-gold hover:bg-gold/90 text-royal-deep font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {status.details_submitted ? "Finish payout setup" : "Set up payouts"}
          </Button>
        </>
      )}
    </section>
  );
}
